import {createSoftControlledHistory, GatedSwitch} from '@raison/navigation';
import {MutationCache, QueryCache, QueryClient} from '@tanstack/react-query';
import {createStore as createJotaiStore} from 'jotai';
import React, {Suspense, useInsertionEffect, useReducer} from 'react';
import {LoadingScreen, NotFoundPage} from '~/entities/fallbacks';
import {setDispatch} from '~/entities/user';
import {createDialogsRegistry} from '~/library/ui-kit';
import {translateAndSendError} from '~/processes/app-maintenance';
import {createAppStore} from '~/redux/store';
import {AppAutoUpdater} from '../processes/app-maintenance';
import {useAppDispatch} from '../shared/store';
import {createRsnApiClient, createRsnPublicApiClient, createRsnUploadClient, with401Interceptor} from './api/client';
import {AppDevtools} from './AppDevtools';
import {AppEffects} from './AppEffects';
import {AppModals} from './AppModals';
import {AppProviders} from './AppProviders';
import {AppRouter} from './AppRouter';
import {routes} from './routes';

const history = createSoftControlledHistory();

// Using memo to make sure queryClient, stores and clients don't get randomly recreated in case something else renders Application.
// Creating them **inside** Application to minimize module-level-side-effects
export const Application = React.memo(function Application() {
    const queryClient = new QueryClient({
        queryCache: new QueryCache({onError: translateAndSendError}),
        mutationCache: new MutationCache({onError: translateAndSendError}),
        defaultOptions: {
            queries: {
                // turned off refetch to closely match current behavior, need to turn back on when needed
                refetchIntervalInBackground: false,
                refetchOnMount: false,
                refetchOnReconnect: false,
                refetchOnWindowFocus: false,
                retry: false,
            },
        },
    });

    const jotaiStore = createJotaiStore();

    const httpClientPublic = createRsnPublicApiClient();
    const networkCfg = {
        enhancers: [with401Interceptor(() => reduxStore)],
    };
    const httpClient = createRsnApiClient(networkCfg);
    const uploadClient = createRsnUploadClient(networkCfg);

    const dialogs = createDialogsRegistry();

    const [key, setKey] = useReducer(i => i + 1, 0);

    const reduxStore = createAppStore({
        key,
        queryClient,
        history,
        getAtom: jotaiStore.get,
        httpClient,
        httpClientPublic,
        uploadClient,
    });

    return (
        <AppProviders
            key={key}
            reduxStore={reduxStore}
            jotaiStore={jotaiStore}
            queryClient={queryClient}
            httpClient={httpClient}
            httpClientPublic={httpClientPublic}
            uploadClient={uploadClient}
            dialogs={dialogs}
        >
            <AppRouter history={history} updateKey={setKey}>
                <Suspense fallback={<LoadingScreen />}>
                    <GatedSwitch routes={routes} fallback={<NotFoundPage />} />
                </Suspense>
                <AppModals />
                <AppEffects />
                <AppAutoUpdater />
            </AppRouter>
            <EvilDispatchHackSetter />
            {import.meta.env.DEV && <AppDevtools />}
        </AppProviders>
    );
});

function EvilDispatchHackSetter() {
    const dispatch = useAppDispatch();
    useInsertionEffect(() => {
        setDispatch(dispatch);
    }, [dispatch]);
    return null;
}
